import {
  BFF_ROUTES,
  DraftQuoteResultSchema,
  EstimateResponseSchema,
  type DraftQuoteRequest,
  type DraftQuoteResult,
  type EstimateResponse,
  type HomeProfileSize,
} from '@rheora/enrollment-contract';

import { bffGet, bffPost } from './bff-client';

/**
 * Typed fetchers for the E-SF04 personalization step. Thin: build the path,
 * validate against the shared contract schema, return. Pages call the
 * `estimate.queries` hooks, never these.
 *
 * ── PUBLIC POSTURE ─────────────────────────────────────────────────────────
 * Credential-free (`bff-client`): no Authorization, no x-tenant-id. The browser
 * holds only the opaque `candidateId` — never the ESIID — and the DRAFT quote's
 * id stays server-side (AC3): `postDraftQuote` returns only `{ status }`.
 *
 * ⚠️ THE BROWSER NOW SENDS A kWh FIGURE, AND THAT IS A REVIEWED CHANGE (E-SF19).
 * It previously sent only a three-member size token, on the reasoning that a
 * client which can post a kWh can choose the usage basis a regulated §25.478
 * deposit is derived from. That reasoning held until it produced a customer-facing
 * falsehood — see `EstimateArgs.declaredMonthlyKwh`. The figure is bounded by the
 * contract, the server still prefers a real GMO usage read over it, and the size
 * token still travels as the documented fallback.
 */

export interface EstimateArgs {
  readonly candidateId: string;
  readonly planId: string;
  /** Optional E-SF02 needs-quiz size — used only for the AC1 profile fallback. */
  readonly homeProfile?: HomeProfileSize;
  /**
   * ⚠️ THE SHOPPER'S OWN MONTHLY kWh (E-SF19 P0a) — the whole point of this epic.
   *
   * The consumption step collects a precise figure and it used to stop at the
   * network edge: only a three-member home-size token could travel, so a shopper
   * who declared 120 kWh/mo was priced at 500 and shown a bill ~180% over, under
   * the caption "we couldn't find usage history for this address". Their own number
   * had been discarded and the screen invented a reason for the substitution.
   *
   * Bounded by the contract (`DeclaredMonthlyKwhSchema`, 50–10,000 kWh/mo) because
   * the SAME figure sizes the PUCT §25.478 deposit. `declaredMonthlyKwhFor` in
   * `usage.service` decides whether the shopper's selection is usable; a value it
   * refuses is simply not sent, and the BFF falls back to history then to the
   * labelled profile.
   */
  readonly declaredMonthlyKwh?: number;
}

/** `GET /bff/estimate` — the personalization estimate + effective-rate curve. */
export async function getEstimate(args: EstimateArgs): Promise<EstimateResponse> {
  const params = new URLSearchParams({ candidate: args.candidateId, plan: args.planId });
  if (args.homeProfile !== undefined) params.set('profile', args.homeProfile);
  // `?usage=` is WRITTEN THROUGH to the funnel session by the BFF, exactly like
  // `?profile=`, so the credit step sizes the deposit off the number the customer
  // is reading on this screen rather than a second, independently-derived one.
  if (args.declaredMonthlyKwh !== undefined) {
    params.set('usage', String(args.declaredMonthlyKwh));
  }
  const path = `${BFF_ROUTES.estimate}?${params.toString()}`;
  return bffGet(path, EstimateResponseSchema);
}

/** `POST /bff/quote/draft` — create the DRAFT web_self_serve quote (blocker 3). */
export async function postDraftQuote(request: DraftQuoteRequest): Promise<DraftQuoteResult> {
  return bffPost(BFF_ROUTES.quoteDraft, request, DraftQuoteResultSchema);
}
